/**
 * Issue 级卡顿火焰图归并。
 * 只消费已经落库的 jankFlameGraphJson / jankV2_samples.json，不改聚类键。
 */

const SOURCE_FILE_RE = /\((?:[^()\n]*[\\/])?([^\\/()]+\.(?:cpp|cc|c|h|hpp|java|cs|mm|m)):(\d+)(?::\d+)?\)/g;
const FUNC_OFFSET_RE = /\(([A-Za-z_][\w:.$<>~]*)\+\d+\)/g;

export function normalizeJankFrameName(line) {
  if (line == null) {
    return '';
  }
  let text = String(line).trim();
  if (!text) {
    return '';
  }
  text = text.replace(/^#\d+\s+pc\s+[0-9a-fA-F]+\s+/i, '');
  text = text.replace(/\s*\[[^\]]*\]\s*$/, '');
  text = text.replace(/\s*\(offset\s+0x[0-9a-fA-F]+\)/gi, '');
  const inlineAt = text.indexOf('([Inline]');
  if (inlineAt >= 0) {
    text = text.slice(0, inlineAt);
  }
  text = text.replace(SOURCE_FILE_RE, '($1:$2)');
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length > 0 && /[/\\]/.test(parts[0])) {
    const token = parts[0];
    const slash = Math.max(token.lastIndexOf('/'), token.lastIndexOf('\\'));
    parts[0] = token.slice(slash + 1);
  }
  text = parts.join(' ').replace(FUNC_OFFSET_RE, '($1)');
  return text.replace(/\s+/g, ' ').trim();
}

function sortTree(node) {
  node.children.sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
  node.children.forEach(sortTree);
  return node;
}

function addSamplePath(root, frames, weight) {
  root.value += weight;
  let node = root;
  frames.forEach((name) => {
    let child = node.children.find(item => item.name === name);
    if (!child) {
      child = { name, value: 0, reportHits: 1, children: [] };
      node.children.push(child);
    }
    child.value += weight;
    node = child;
  });
}

function framesOfStack(stack) {
  return String(stack || '')
    .split('\n')
    .map(normalizeJankFrameName)
    .filter(Boolean)
    .reverse();
}

export function buildFlameGraphFromJankV2Samples(input) {
  if (input == null || input === '') {
    return null;
  }
  let data = input;
  if (typeof input === 'string') {
    try {
      data = JSON.parse(input);
    } catch (e) {
      return null;
    }
  }
  if (!data || !Array.isArray(data.samples)) {
    return null;
  }
  const root = { name: 'all', value: 0, reportHits: 1, children: [] };
  data.samples.forEach((sample) => {
    if (!sample) {
      return;
    }
    const frames = framesOfStack(sample.stack);
    if (frames.length === 0) {
      return;
    }
    const times = sample.times;
    const weight = Array.isArray(times) && times.length > 0 ? times.length : 1;
    addSamplePath(root, frames, weight);
  });
  if (!root.value || root.children.length === 0) {
    return null;
  }
  if (root.children.length === 1) {
    return sortTree(root.children[0]);
  }
  return sortTree(root);
}

function cloneNode(node) {
  if (!node || typeof node !== 'object') {
    return null;
  }
  const value = Number(node.value);
  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }
  const name = normalizeJankFrameName(node.name) || String(node.name || 'unknown');
  const children = [];
  (node.children || []).forEach((child) => {
    const cloned = cloneNode(child);
    if (cloned) {
      children.push(cloned);
    }
  });
  const reportHits = Number(node.reportHits) > 0 ? Number(node.reportHits) : 1;
  return { name, value, reportHits, children };
}

function foldSameName(nodes) {
  const acc = {
    name: nodes[0].name,
    value: 0,
    reportHits: 0,
    children: [],
  };
  const groups = new Map();
  nodes.forEach((node) => {
    acc.value += node.value;
    acc.reportHits += node.reportHits;
    node.children.forEach((child) => {
      if (!groups.has(child.name)) {
        groups.set(child.name, []);
      }
      groups.get(child.name).push(child);
    });
  });
  acc.children = [...groups.values()]
    .map(foldSameName)
    .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
  return acc;
}

export function mergeJankFlameGraphTrees(trees) {
  const nodes = [];
  (trees || []).forEach((tree) => {
    try {
      const node = cloneNode(tree);
      if (node) {
        nodes.push(node);
      }
    } catch (e) {
      // 坏节点跳过，不让整页白屏
    }
  });
  if (nodes.length === 0) {
    return null;
  }
  const rootNames = new Set(nodes.map(node => node.name));
  if (rootNames.size === 1) {
    return foldSameName(nodes);
  }
  return foldSameName(nodes.map(node => ({
    name: 'all',
    value: node.value,
    reportHits: node.reportHits,
    children: [node],
  })));
}

function hottestLeaf(tree) {
  let node = tree;
  if (node && node.name === 'all' && node.children && node.children.length > 0) {
    node = node.children[0];
  }
  const names = [];
  let guard = 0;
  while (node && guard < 80) {
    names.push(node.name);
    const next = (node.children || [])[0];
    if (!next) {
      break;
    }
    node = next;
    guard += 1;
  }
  return { names, leaf: node };
}

export function summarizeJankFlameGraph(tree) {
  if (!tree || !Number(tree.value)) {
    return null;
  }
  const { names, leaf } = hottestLeaf(tree);
  if (!leaf) {
    return null;
  }
  const sampleCount = Number(tree.value);
  const totalReports = Number(tree.reportHits) > 0 ? Number(tree.reportHits) : 1;
  return {
    totalReports,
    sampleCount,
    hotspotPath: names.join(' → '),
    hotspotPercent: sampleCount ? (Number(leaf.value) / sampleCount) * 100 : 0,
    hotspotReportHits: Number(leaf.reportHits) || 0,
  };
}

export const ISSUE_JANK_FLAME_SAMPLE_ROWS = 15;

function parseExistingTree(raw) {
  if (!raw) {
    return null;
  }
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!data || !Number(data.value)) {
      return null;
    }
    return data;
  } catch (e) {
    return null;
  }
}

async function mapPool(items, limit, fn) {
  const results = new Array(items.length);
  let cursor = 0;
  const worker = async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      // eslint-disable-next-line no-await-in-loop
      results[index] = await fn(items[index]);
    }
  };
  const width = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: width }, worker));
  return results;
}

export function planIssueJankListQuery({ appId, pid, issueId, fetchSamples, parsedJank }) {
  return {
    url: '/api/crash/queryCrashList',
    body: {
      appId,
      platformId: pid,
      issueId,
      start: 0,
      rows: ISSUE_JANK_FLAME_SAMPLE_ROWS,
      needEsMapList: true,
      // type=40 的 jankFlameGraphJson 从 CRASH_DETAIL 现算。JankV2 与拆分卡顿不需要这份还原堆栈。
      needRetracedStack: !fetchSamples && !parsedJank,
      needQueryCustomKv: false,
    },
  };
}

export function planParsedJankRawQuery({ appId, expUid }) {
  return {
    url: '/api/crash/queryCrashList',
    body: {
      appId,
      start: 0,
      rows: 1,
      needEsMapList: true,
      needRetracedStack: true,
      searchConditionGroup: {
        conditions: [
          { field: 'expUid', queryType: 'TERM', term: expUid },
          { field: 'exceptionType', queryType: 'TERM', term: 'JankRaw' },
        ],
      },
    },
    options: { silentFailure: true },
  };
}

export function planJankV2AttachmentQuery({ appId, pid, crashId }) {
  return {
    url: '/redir/api/crash/queryAppDetailCrash',
    body: {
      appId,
      pid,
      crashId,
      retraceTimeoutMillis: 3000,
    },
    options: { silentFailure: true },
  };
}

function treeFromSamplesContent(content) {
  if (!content) {
    return null;
  }
  return buildFlameGraphFromJankV2Samples(content);
}

export async function loadIssueJankFlameTrees({
  appId,
  pid,
  issueId,
  fetchSamples,
  parsedJank,
  post,
}) {
  const listPlan = planIssueJankListQuery({ appId, pid, issueId, fetchSamples, parsedJank });
  const rsp = await post(listPlan.url, listPlan.body);
  const ret = (rsp && rsp.json && rsp.json.ret) || {};
  const crashIds = (ret.crashIdList || []).slice(0, ISSUE_JANK_FLAME_SAMPLE_ROWS);
  const crashDatas = ret.crashDatas || {};
  const esMapList = ret.esMapList || [];
  const trees = [];
  let skipped = 0;

  if (parsedJank) {
    const expUids = [];
    const seen = new Set();
    crashIds.forEach((crashId) => {
      const esMap = esMapList.find(item => item && item.crashId === crashId) || {};
      const crashMap = crashDatas[crashId] || {};
      const expUid = esMap.expUid || crashMap.expUid;
      if (!expUid || seen.has(expUid)) {
        if (!expUid) {
          skipped += 1;
        }
        return;
      }
      seen.add(expUid);
      expUids.push(expUid);
    });
    const loaded = await mapPool(expUids, 4, async (expUid) => {
      try {
        const rawPlan = planParsedJankRawQuery({ appId, expUid });
        const rawRsp = await post(rawPlan.url, rawPlan.body, rawPlan.options);
        const rawMap = Object.values((rawRsp.json && rawRsp.json.ret && rawRsp.json.ret.crashDatas) || {})[0];
        return parseExistingTree(rawMap && rawMap.jankFlameGraphJson);
      } catch (e) {
        return null;
      }
    });
    loaded.forEach((tree) => {
      if (tree) {
        trees.push(tree);
      } else {
        skipped += 1;
      }
    });
  } else {
    const needAttach = [];
    crashIds.forEach((crashId) => {
      const tree = parseExistingTree(crashDatas[crashId] && crashDatas[crashId].jankFlameGraphJson);
      if (tree) {
        trees.push(tree);
        return;
      }
      if (fetchSamples) {
        needAttach.push(crashId);
        return;
      }
      skipped += 1;
    });
    if (needAttach.length > 0) {
      const loaded = await mapPool(needAttach, 4, async (crashId) => {
        try {
          const attachPlan = planJankV2AttachmentQuery({ appId, pid, crashId });
          const detail = await post(attachPlan.url, attachPlan.body, attachPlan.options);
          const attachList = (detail.json && detail.json.data && detail.json.data.attachList) || [];
          const file = attachList.find(item => item.fileName === 'jankV2_samples.json');
          return treeFromSamplesContent(file && file.content);
        } catch (e) {
          return null;
        }
      });
      loaded.forEach((tree) => {
        if (tree) {
          trees.push(tree);
        } else {
          skipped += 1;
        }
      });
    }
  }

  return {
    tree: mergeJankFlameGraphTrees(trees),
    skipped,
    merged: trees.length,
  };
}
